import {useEffect, useMemo, useState} from "react";
import {createServiceRequest} from "../../api/client";

const vehicleTypeLabels = {
    truck: "Грузовой автомобиль",
    tractor: "Тягач",
    trailer: "Прицеп / полуприцеп",
    bus: "Автобус",
    special: "Спецтехника",
    other: "Другое",
};
const vehicleModeOptions = [
    {value: "saved", label: "Использовать сохраненный автомобиль"},
    {value: "manual", label: "Ввести другую технику"},
];

function getAccountDetailsStorageKey(user) {
    if (!user) {
        return null;
    }

    return `truckcenter-account-details-${user.id || user.phone}`;
}

function readAccountDetails(user) {
    const storageKey = getAccountDetailsStorageKey(user);

    if (!storageKey) {
        return null;
    }

    try {
        const storedDetails = window.localStorage.getItem(storageKey);
        return storedDetails ? JSON.parse(storedDetails) : null;
    } catch {
        return null;
    }
}

function buildSavedVehicleSummary(details) {
    if (!details) {
        return "";
    }

    const mainParts = [details.vehicleBrand, details.vehicleModel, details.vehicleYear]
        .filter(Boolean)
        .join(" ");
    const detailParts = [
        details.vehicleType ? vehicleTypeLabels[details.vehicleType] || details.vehicleType : "",
        details.vehiclePlate ? `госномер ${details.vehiclePlate}` : "",
        details.vehicleVin ? `VIN ${details.vehicleVin}` : "",
        details.vehicleMileage ? `${details.vehicleMileage} км` : "",
    ].filter(Boolean);

    return [mainParts, ...detailParts].filter(Boolean).join(" · ").slice(0, 120);
}

function Request({auth, onContactClick}) {
    const user = auth?.user;
    const accountDetails = useMemo(() => readAccountDetails(user), [user]);
    const savedVehicleSummary = useMemo(() => buildSavedVehicleSummary(accountDetails), [accountDetails]);
    const autofillName = accountDetails?.fullName || user?.fullName || "";
    const autofillPhone = accountDetails?.phone || user?.phone || "";
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState("");
    const [submitError, setSubmitError] = useState("");
    const [vehicleMode, setVehicleMode] = useState("manual");
    const [isVehicleModeOpen, setIsVehicleModeOpen] = useState(false);
    const [vehicleValue, setVehicleValue] = useState("");

    useEffect(() => {
        if (savedVehicleSummary) {
            setVehicleMode("saved");
            setVehicleValue(savedVehicleSummary);
            return;
        }

        setVehicleMode("manual");
        setVehicleValue("");
    }, [savedVehicleSummary]);

    function handleVehicleModeSelect(nextMode) {
        setVehicleMode(nextMode);
        setVehicleValue(nextMode === "saved" ? savedVehicleSummary : "");
        setIsVehicleModeOpen(false);
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus("");
        setSubmitError("");

        const form = event.currentTarget;
        const formData = new FormData(form);

        try {
            await createServiceRequest({
                customerName: formData.get("customerName"),
                phone: formData.get("phone"),
                vehicle: vehicleMode === "saved" && savedVehicleSummary ? savedVehicleSummary : vehicleValue,
                serviceType: formData.get("serviceType"),
                message: formData.get("message"),
            });

            form.reset();
            setVehicleValue(savedVehicleSummary || "");
            setVehicleMode(savedVehicleSummary ? "saved" : "manual");
            setSubmitStatus("Заявка отправлена. Мы свяжемся с вами.");
        } catch (requestError) {
            setSubmitError(requestError.message);
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <main className="request-page">
            <section className="request-hero">
                <span className="request-eyebrow">Заявка на обслуживание</span>
                <h1>Оставить заявку</h1>
                <p>
                    Заполните форму, если вопрос не срочный. Заявки попадают в админку и обрабатываются отдельно от
                    прямых сообщений.
                </p>
            </section>

            <section className="request-grid">
                <aside className="request-priority-card">
                    <span>Быстрее всего</span>
                    <h2>Свяжитесь напрямую</h2>
                    <p>
                        Telegram, WhatsApp, MAX и звонок по номеру — приоритетные каналы. Их проверяют чаще, чем
                        входящие заявки на сайте.
                    </p>
                    <button className="request-contact-button" type="button" onClick={onContactClick}>
                        Открыть контакты
                    </button>
                </aside>

                <form className="request-card" onSubmit={handleSubmit}>
                    <div className="request-card-header">
                        <h2>Данные заявки</h2>
                        <p>Чем точнее описание, тем быстрее получится оценить работу.</p>
                    </div>

                    {submitStatus && <p className="request-status">{submitStatus}</p>}
                    {submitError && <p className="request-status request-status-error">{submitError}</p>}

                    <label>
                        Ваше имя
                        <input
                            type="text"
                            name="customerName"
                            autoComplete="name"
                            defaultValue={autofillName}
                            maxLength="120"
                            required
                        />
                    </label>

                    <label>
                        Телефон
                        <input
                            type="tel"
                            name="phone"
                            autoComplete="tel"
                            inputMode="tel"
                            defaultValue={autofillPhone}
                            placeholder="+7 950 000 00 00"
                            pattern="^\+?[0-9\s()\-]{10,20}$"
                            maxLength="20"
                            required
                        />
                    </label>

                    <div className="request-form-row">
                        {savedVehicleSummary && (
                            <div className="request-saved-vehicle">
                                <span>Автомобиль из личного кабинета</span>
                                <div
                                    className="request-vehicle-dropdown"
                                    data-open={isVehicleModeOpen}
                                    onBlur={(event) => {
                                        if (!event.currentTarget.contains(event.relatedTarget)) {
                                            setIsVehicleModeOpen(false);
                                        }
                                    }}
                                >
                                    <button
                                        className="request-vehicle-toggle"
                                        type="button"
                                        aria-expanded={isVehicleModeOpen}
                                        aria-haspopup="listbox"
                                        onClick={() => setIsVehicleModeOpen((isOpen) => !isOpen)}
                                    >
                                        <span key={vehicleMode}>
                                            {vehicleModeOptions.find((option) => option.value === vehicleMode)?.label}
                                        </span>
                                        <span className="request-vehicle-arrow" aria-hidden="true"/>
                                    </button>
                                    <div className="request-vehicle-list" role="listbox" aria-label="Автомобиль из личного кабинета">
                                        {vehicleModeOptions.map((option) => (
                                            <button
                                                className="request-vehicle-option"
                                                data-selected={vehicleMode === option.value}
                                                key={option.value}
                                                type="button"
                                                role="option"
                                                aria-selected={vehicleMode === option.value}
                                                onClick={() => handleVehicleModeSelect(option.value)}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        <label>
                            Техника
                            <input
                                type="text"
                                name="vehicle"
                                value={vehicleValue}
                                onChange={(event) => setVehicleValue(event.target.value)}
                                readOnly={vehicleMode === "saved" && Boolean(savedVehicleSummary)}
                                maxLength="120"
                                placeholder="MAN TGX"
                            />
                        </label>

                        <label>
                            Услуга
                            <input type="text" name="serviceType" maxLength="120" placeholder="Диагностика"/>
                        </label>
                    </div>

                    <label>
                        Комментарий
                        <textarea name="message" maxLength="2000" placeholder="Кратко опишите задачу"/>
                    </label>

                    <button className="request-submit" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Отправляем..." : "Отправить заявку"}
                    </button>
                </form>
            </section>
        </main>
    );
}

export default Request;
